import api from './client';
import { bots } from '../../data/bots';
import type { ApiResponse, UsageHistoryResponse } from '../../types/api';
import { createLogger } from '../../utils/logger';

const logger = createLogger('usage-api');

export const getUsageHistory = async (startDate: string, endDate: string): Promise<UsageHistoryResponse> => {
  try {
    logger.info('Fetching usage history', { startDate, endDate });

    const response = await api.get<any, UsageHistoryResponse>('/api/usage/history', {
      params: { startDate, endDate }
    });

    if (!response.success) {
      throw new Error('Failed to fetch usage history');
    }

    // Process usage data to include bot details
    const processedUsage = (response.usage || []).map(usage => {
      const bot = bots.find(b => b.id === usage.botId) || 
                 bots.flatMap(b => b.subBots || []).find(sb => sb.id === usage.botId);
      
      return {
        ...usage,
        botTitle: bot?.title || 'Unknown Bot',
        creditsUsed: Number(usage.creditsUsed) || bot?.price || 0
      };
    });

    logger.info('Processed usage data', { 
      records: processedUsage.length,
      totalCredits: processedUsage.reduce((sum, u) => sum + u.creditsUsed, 0)
    });

    return {
      success: true,
      usage: processedUsage
    };
  } catch (error) {
    logger.error('Failed to fetch usage history', error);
    throw error;
  }
};

export const recordUsage = async (usageData: {
  botId: string;
  instanceId: string;
  creditsUsed: number;
}): Promise<ApiResponse> => {
  try {
    logger.info('Recording usage', usageData);
    const response = await api.post<any, ApiResponse>('/api/usage/record', usageData);
    logger.info('Usage recorded successfully');
    return response;
  } catch (error) {
    logger.error('Failed to record usage', error);
    throw error;
  }
};

export const getUsageLogs = async (startDate: string, endDate: string): Promise<ApiResponse> => {
  try {
    logger.info('Fetching usage logs', { startDate, endDate });
    const response = await api.get<any, ApiResponse>('/api/usage/logs', {
      params: { startDate, endDate }
    });
    logger.info('Usage logs fetched successfully', { count: response.data?.length });
    return response;
  } catch (error) {
    logger.error('Failed to fetch usage logs', error);
    throw error;
  }
};

export const getRevenueReport = async (startDate: string, endDate: string): Promise<ApiResponse> => {
  try {
    logger.info('Fetching revenue report', { startDate, endDate });
    const response = await api.get<any, ApiResponse>('/api/usage/revenue', {
      params: { startDate, endDate }
    });
    logger.info('Revenue report fetched successfully');
    return response;
  } catch (error) {
    logger.error('Failed to fetch revenue report', error);
    throw error;
  }
};